import React from 'react'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@src/components/base'
import { NutritionistSpecialtiesList } from '@src/components/NutritionistSpecialtiesList'
import { primaryGoalSelector } from '@src/selectors/app'
import { AllCoachProfilesQuery } from '@src/graphql/fetchAllCoachProfiles.generated'
import { COACH_SPECIALTIES_BY_USER_GOAL_NAME } from '../../../constants'
import { CoachInfo } from './CoachInfo'

interface CoachCardProps {
  coachProfile: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0]
  onCoachSelect: (
    coachProfile: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0],
  ) => void
  generateButtonText?: (
    coachProfile: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0],
  ) => string
}

export const CoachCard = ({ coachProfile, onCoachSelect, generateButtonText }: CoachCardProps) => {
  const styles = useStyleSheet(themedStyles)

  const primaryGoal = useSelector(primaryGoalSelector)

  const buttonText = generateButtonText?.(coachProfile) || 'Select'

  const isGreatFit = () => {
    if (!primaryGoal) {
      return false
    }

    const specialtiesByUserGoal = COACH_SPECIALTIES_BY_USER_GOAL_NAME[primaryGoal.kind] || []
    if (specialtiesByUserGoal.length === 0) {
      return false
    }

    return coachProfile.specialties
      .map((s) => s.key)
      .some((specialty) => specialtiesByUserGoal.includes(specialty))
  }

  return (
    <View style={styles.container}>
      <CoachInfo coachProfile={coachProfile} />
      <View style={styles.specialtiesContainer}>
        <View style={styles.specialtiesHeader}>
          <Text type="small">Specialties</Text>
          {isGreatFit() && (
            <View style={styles.greatFitContent}>
              <Icon name="star" weight="fill" style={styles.star} size={16} />
              <Text type="small" style={styles.greatFitText}>
                Great fit for you!
              </Text>
            </View>
          )}
        </View>
        <NutritionistSpecialtiesList specialties={coachProfile.specialties} />
      </View>
      <Button
        type="primary"
        size="block"
        onPress={() => onCoachSelect(coachProfile)}
        accessibilityLabel={buttonText}
        style={styles.selectButton}
      >
        {buttonText}
      </Button>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    padding: 16,
    borderColor: 'theme.surface.base1',
    borderWidth: 2,
    borderRadius: 24,
  },
  infoContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  infoContent: {
    flex: 1,
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  coachPhoto: {
    width: 48,
    height: 48,
    borderRadius: 52,
    marginBottom: 16,
  },
  specialtiesContainer: {
    marginTop: 8,
  },
  specialtiesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  greatFitContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  greatFitText: {
    fontWeight: '500',
  },
  star: {
    color: 'theme.marketing.yellow',
  },
  selectButton: {
    marginTop: 24,
    height: 44,
  },
})
